import axios, { AxiosRequestConfig } from "axios";
import { identity, pickBy } from "lodash";
import { CashManagementAccount } from "../models/CashManagement";
import { UUID } from "../utils";
import getErrorDetails from "./helpers/getErrorDetails";
import paramsSerializer from "./helpers/paramsSerializer";

export type GetCashManagementAccounts = (
  id: UUID,
  active: boolean | undefined,
  options?: AxiosRequestConfig
) => Promise<{ list: CashManagementAccount[] }>;

export type GetCashManagementAccount = (
  id: UUID,
  options?: AxiosRequestConfig
) => Promise<CashManagementAccount>;

export type PostCashManagementAccount = (
  id: UUID,
  data: CashManagementAccount,
  options?: AxiosRequestConfig
) => Promise<CashManagementAccount>;

export type PatchCashManagementAccount = (
  id: UUID,
  data: CashManagementAccount,
  options?: AxiosRequestConfig
) => Promise<CashManagementAccount>;

export type TestCashManagementSettlement = (
  id: UUID,
  options?: AxiosRequestConfig
) => Promise<any>;

export const getCashManagementAccounts: GetCashManagementAccounts = async (
  id,
  active,
  options
) => {
  try {
    const response = await axios({
      ...options,
      method: "GET",
      url: `/accounts`,
      params: {
        virtual: true,
        customer: id,
        active: active !== undefined ? active : undefined,
      },
      paramsSerializer,
    });
    const { data = {} } = response;
    return { list: data };
  } catch (error) {
    throw getErrorDetails(error);
  }
};

export const getCashManagementAccount: GetCashManagementAccount = async (
  id,
  options
) => {
  try {
    const response = await axios({
      ...options,
      method: "GET",
      url: `/accounts/${id}`,
    });
    const { data = {} } = response;
    return data;
  } catch (error) {
    throw getErrorDetails(error);
  }
};

export const patchCashManagementAccount: PatchCashManagementAccount = async (
  id,
  account,
  options
) => {
  try {
    const response = await axios({
      ...options,
      method: "PUT",
      url: `/accounts/${id}`,
      data: {
        ...pickBy(
          {
            ...account,
            virtual: true,
            country: account.country?.id || null,
            currency: account.currency?.id || null,
            cashManagementProviderAccount: {
              cashManagementProvider:
                account.cashManagementProviderAccount?.cashManagementProvider
                  ?.id ?? null,
              providerAccountId:
                account.cashManagementProviderAccount?.providerAccountId ||
                null,

              settlementAutomatic:
                account.cashManagementProviderAccount?.settlementAutomatic ??
                false,
            },
          },
          identity
        ),
        active: account.active,
      },
    });
    const { data = {} } = response;
    return await getCashManagementAccount(data.id, options);
  } catch (error) {
    throw getErrorDetails(error);
  }
};

export const postCashManagementAccount: PostCashManagementAccount = async (
  id,
  account,
  options
) => {
  try {
    const response = await axios({
      ...options,
      method: "POST",
      url: `/accounts`,
      data: pickBy(
        {
          ...account,
          customer: id,
          virtual: true,
          cashManagementProviderAccount: {
            cashManagementProvider:
              account.cashManagementProviderAccount?.cashManagementProvider
                ?.id ?? null,
            providerAccountId:
              account.cashManagementProviderAccount?.providerAccountId || null,
            settlementAutomatic:
              account.cashManagementProviderAccount?.settlementAutomatic ??
              false,
          },
          country: account.country.id,
          currency: account.currency.id,
          swiftBic: account.swiftBic,
        },
        identity
      ),
    });
    const { data = {} } = response;
    return await getCashManagementAccount(data.id, options);
  } catch (error) {
    throw getErrorDetails(error);
  }
};

export const testCashManagementSettlement: TestCashManagementSettlement = async (
  id: UUID,
  options?: AxiosRequestConfig
) => {
  try {
    const response = await axios({
      ...options,
      method: "POST",
      url: `/customer/${id}/settlement/trigger-mock`,
    });
    const { data = {} } = response;
    return data;
  } catch (error) {
    throw getErrorDetails(error);
  }
};
